/**
 * Axios instance for all API calls. Attaches Bearer token on every request and
 * silently refreshes the session on 401 (except for /auth/ endpoints).
 */
import axios from 'axios'
import { useAuthStore } from '@/store/auth.store'

export const apiClient = axios.create({
  baseURL: '/api/v1',
  withCredentials: true,
})

// Attach Bearer token to every request
apiClient.interceptors.request.use((config) => {
  const token = useAuthStore.getState().accessToken
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }
  return config
})

let isRefreshing = false
let refreshQueue: Array<(token: string) => void> = []

function drainQueue(token: string) {
  refreshQueue.forEach((cb) => cb(token))
  refreshQueue = []
}

// Silent refresh on 401, except for /auth/ endpoints
apiClient.interceptors.response.use(
  (res) => res,
  async (err) => {
    const original = err.config
    const url: string = original?.url ?? ''

    // Do NOT redirect on 401 for auth endpoints
    if (err.response?.status === 401 && url.startsWith('/auth/')) {
      return Promise.reject(err)
    }

    if (err.response?.status === 401 && !original._retry) {
      original._retry = true

      if (isRefreshing) {
        return new Promise((resolve) => {
          refreshQueue.push((token) => {
            original.headers.Authorization = `Bearer ${token}`
            resolve(apiClient(original))
          })
        })
      }

      isRefreshing = true
      try {
        const { data } = await axios.post('/api/v1/auth/refresh', {}, { withCredentials: true })
        const newToken: string = data.accessToken
        useAuthStore.getState().setAuth(newToken, data.account)
        drainQueue(newToken)
        original.headers.Authorization = `Bearer ${newToken}`
        return apiClient(original)
      } catch {
        useAuthStore.getState().clearAuth()
        return Promise.reject(err)
      } finally {
        isRefreshing = false
      }
    }

    return Promise.reject(err)
  },
)
